import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import LoadingIndicator from '@/components/LoadingIndicator';
import Pagination from '@/components/Pagination';
import { SearchInput } from '@/components/SearchInput';
import { DataTable, ColumnDefinition, CellText, Pill } from '@/components/shared/DataTable';
import { Link as LinkIcon, Trash2, MoveRight } from 'lucide-react';
import { toast } from 'sonner';
import SectionCard from './shared/SectionCard';
import SiteDiscovery from './SiteDiscovery';
import { useGetLinks } from '@/hooks/useBot';
import {
  addLink,
  removeLink,
  updateRefreshIntervalByPattern,
  updateAllRefreshIntervals,
} from '@/services/bots';
import { convertDiscoveredLink } from '@/services/bots-refactor';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { debounce } from 'lodash';
import Image from 'next/image';
import { ChangeEvent, FC, useCallback, useEffect, useMemo, useState } from 'react';
import AddLinkForm from './AddLinkForm';
import { IBaseQueryParams } from '@/types/common';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

interface IAddLinksProps {
  isBackButton?: boolean;
  token: string;
}

interface ILink {
  no: number;
  url: string;
  refresh_interval_days?: number | null;
  last_refresh_time?: string | null;
  next_refresh_time?: string | null;
  processed?: boolean | null;
  created_at?: string;
  source?: 'discovered' | string;
  updated?: boolean | null;
}

enum TABS {
  ADD_NEW_LINK,
  MANAGE_CURRENT_LINKS,
}

const AddLinks: FC<IAddLinksProps> = ({ isBackButton = true, token }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [tab, setTab] = useState(TABS.ADD_NEW_LINK);
  const [modalOpen, setModalOpen] = useState(false);
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [refreshInterval, setRefreshInterval] = useState(7);
  const [selectedPattern, setSelectedPattern] = useState('');
  const [validationError, setValidationError] = useState('');
  const [queryParams, setQueryParams] = useState<{
    page: number;
    limit: number;
    searchText: string;
    status?: string;
  }>({ page: 1, limit: 10, searchText: '', status: '' });

  const [links, setLinks] = useState<ILink[]>([]);
  const [arrLinks, setArrLinks] = useState<any[]>([]);
  const [allLinks, setAllLinks] = useState<ILink[]>([]);

  const { data: linkData, refetch: refetchLinks, isFetching } = useGetLinks(token, queryParams);

  useEffect(() => {
    init();
  }, [tab, token, linkData?.data]);

  const init = () => {
    if (linkData?.data && linkData.data.length > 0) {
      // Ensure each link has the refresh_interval_days from the API response
      const linksWithRefresh = linkData.data.map((link: ILink) => ({
        ...link,
        refresh_interval_days: link.refresh_interval_days || 7, // Only use default if not provided
      }));
      setAllLinks(linksWithRefresh);
    } else {
      setAllLinks([]);
    }
  };

  const clearUrlsInParent = () => {
    setLinks([]);
    setArrLinks([]);
  };

  const handleRefreshIntervalChange = (value: number) => {
    const minDays = selectedPattern ? 1 : 7;
    if (value < minDays) {
      setValidationError(`Minimum refresh interval is ${minDays} day${minDays > 1 ? 's' : ''}`);
    } else {
      setValidationError('');
    }
    setRefreshInterval(value);
  };

  // Helper function to format pattern as user types
  const handlePatternChange = (value: string) => {
    // Store the pattern without slashes for API compatibility
    const cleanPattern = value.replace(/^\/+|\/+$/g, '');
    setSelectedPattern(cleanPattern);

    // Reset refresh interval if switching between pattern/all
    setRefreshInterval(cleanPattern ? Math.max(1, refreshInterval) : Math.max(7, refreshInterval));
    setValidationError('');
  };

  const handleSaveSettings = async () => {
    const minDays = selectedPattern ? 1 : 7;
    if (refreshInterval < minDays) {
      setValidationError(`Minimum refresh interval is ${minDays} day${minDays > 1 ? 's' : ''}`);
      return;
    }

    try {
      setIsLoading(true);

      if (selectedPattern) {
        // Send the pattern directly without adding slashes
        await updateRefreshIntervalByPattern(selectedPattern, refreshInterval, token);
      } else {
        await updateAllRefreshIntervals(refreshInterval, token);
      }

      toast.success('Refresh settings updated successfully');
      setSettingsModalOpen(false);
      setValidationError('');
      refetchLinks();
    } catch {
      toast.error('Failed to update refresh settings');
    } finally {
      setIsLoading(false);
    }
  };

  const onTraining = async () => {
    // Check if any links are selected for training
    const selectedLinks = arrLinks.filter((link) => link.isChecked);

    if (selectedLinks.length === 0) {
      toast.error('Please select at least one link for training.');
      return; // Exit the function early as there's nothing more to do
    }

    // Proceed with training since at least one link is selected
    try {
      setIsLoading(true);
      const arr: any = selectedLinks.map((link) => ({
        link: link.url,
        type: 'website',
      }));
      await addLink(arr, token);
      toast.success('Links have been added successfully.');
      setModalOpen(false);
      refetchLinks();
    } catch {
      toast.error('Sorry, we are currently having issues adding these links');
    } finally {
      setIsLoading(false);
      clearUrlsInParent();
    }
  };

  const onRemoveLink = async (row: ILink) => {
    setIsLoading(true);

    try {
      await removeLink(row.no);
      // Remove the link from allLinks and searchLinks states
      setAllLinks((prevLinks) => prevLinks.filter((link) => link.no !== row.no));
    } catch (error) {
      toast.error(
        `Failed to remove link: ${error instanceof Error ? error.message : 'Please try again.'}`,
      );
    }

    setIsLoading(false);
  };

  /**
   * Handler to convert a discovered link to a training link
   * @param row The link to convert
   */
  const onConvertDiscoveredLink = async (row: ILink) => {
    setIsLoading(true);

    try {
      await convertDiscoveredLink(row.no, token);
      toast.success('Link has been moved to training successfully.');
      refetchLinks(); // Refresh the list after conversion
    } catch (error) {
      toast.error(
        `Failed to move link to training: ${error instanceof Error ? error.message : 'Please try again.'}`,
      );
    }

    setIsLoading(false);
  };

  const handleSearchLink = useCallback(
    debounce((searchText: string) => {
      setQueryParams((prev) => ({
        ...prev,
        page: 1,
        searchText,
      }));
    }, 500),
    [setQueryParams],
  );

  const handleSearchInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
    handleSearchLink(e.target.value);
  };

  const handlePageChange = (page: number) => {
    setQueryParams((prev) => ({
      ...prev,
      page,
    }));
  };

  const handleStatusChange = (value: string) => {
    const newStatus = value === 'all' ? '' : value;
    // Reset to page 1 when filter changes
    setQueryParams((prev) => ({
      ...prev,
      status: newStatus || undefined,
      page: 1,
    }));
  };

  const getStatusVariant = (row: ILink): 'info' | 'success' | 'warning' => {
    if (row.processed === null && row.updated === null && row.next_refresh_time === null) {
      return 'info';
    }
    return row.processed === true ? 'success' : 'warning';
  };

  const getStatusLabel = (row: ILink): string => {
    if (row.processed === null && row.updated === null && row.next_refresh_time === null) {
      return 'Discovered';
    }
    return row.processed === true ? 'Trained' : 'Processing';
  };

  const columns = useMemo<ColumnDefinition<ILink>[]>(
    () => [
      {
        id: 'link',
        label: 'Link',
        accessor: 'url',
        type: 'custom',
        width: '40%',
        sortable: true,
        renderCell: (value) => (
          <div className="flex items-center gap-3">
            <LinkIcon className="h-5 w-5 text-slate-400 flex-shrink-0" />
            <CellText variant="primary" className="break-all">
              {value}
            </CellText>
          </div>
        ),
      },
      {
        id: 'added_on',
        label: 'Added On',
        accessor: 'created_at',
        type: 'custom',
        width: '15%',
        sortable: true,
        renderCell: (value) => (
          <CellText variant="secondary">{value ? value.split('T')[0] : 'N/A'}</CellText>
        ),
      },
      {
        id: 'refresh_interval',
        label: 'Refresh Interval',
        accessor: 'refresh_interval_days',
        type: 'custom',
        width: '15%',
        sortable: true,
        renderCell: (value, row) => {
          const isDiscovered =
            row.processed === null && row.updated === null && row.next_refresh_time === null;
          return isDiscovered ? (
            <CellText variant="secondary" className="text-slate-400">
              Not applicable
            </CellText>
          ) : (
            <CellText variant="secondary">Every {value} days</CellText>
          );
        },
      },
      {
        id: 'last_refreshed',
        label: 'Last Refreshed',
        accessor: 'last_refresh_time',
        type: 'custom',
        width: '15%',
        sortable: true,
        renderCell: (value, row) => {
          const isDiscovered =
            row.processed === null && row.updated === null && row.next_refresh_time === null;
          return isDiscovered ? (
            <CellText variant="secondary" className="text-slate-400">
              Not yet trained
            </CellText>
          ) : (
            <CellText variant="secondary">{value ? value.split('T')[0] : 'Never'}</CellText>
          );
        },
      },
      {
        id: 'status',
        label: 'Training Status',
        accessor: 'processed',
        type: 'custom',
        width: '10%',
        sortable: true,
        renderCell: (_, row) => (
          <Pill
            value={getStatusLabel(row)}
            variant={getStatusVariant(row)}
            showIcon={false}
            className="min-w-[80px] text-center justify-center"
          />
        ),
      },
      {
        id: 'actions',
        label: '',
        accessor: 'no',
        type: 'actions',
        width: '8%',
        align: 'center',
        meta: {
          actions: [
            {
              id: 'convert',
              label: 'Add to Training',
              icon: MoveRight,
              onClick: (row: ILink) => onConvertDiscoveredLink(row),
              // #5012: the DataTable's RowAction contract is `visible` — `hideCondition` was
              // never read, so "Add to Training" rendered on every row, including ones the
              // status column already reported as Trained. Customers read that as unfinished
              // work and asked whether they still had to click it. Show it only where it is
              // actually an action: a Discovered link that has never been trained.
              visible: (row: ILink) => row.processed === null
                && row.updated === null
                && row.next_refresh_time === null,
            },
            {
              id: 'delete',
              label: 'Remove',
              icon: Trash2,
              onClick: (row: ILink) => onRemoveLink(row),
            },
          ],
        },
      },
    ],
    [onRemoveLink, onConvertDiscoveredLink],
  );

  return (
    <>
      <div className='flex gap-4 mb-4 items-center'>
        <SearchInput
          className='max-w-[450px]'
          placeholder='Search links...'
          value={searchText}
          onChange={handleSearchInputChange}
          isLoading={isFetching && !!queryParams?.searchText}
        />
        <Select onValueChange={handleStatusChange} value={queryParams.status || 'all'}>
          <SelectTrigger className='w-[180px] bg-white text-gray-500 font-normal border border-gray-300 rounded-md h-10'>
            <SelectValue placeholder='Filter by status' />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value='all'>All Statuses</SelectItem>
            <SelectItem value='trained'>Trained</SelectItem>
            <SelectItem value='processing'>Processing</SelectItem>
            <SelectItem value='discovered'>Discovered</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {isLoading && <LoadingIndicator />}
      {/* Page discovery sits above the list because it is what keeps the list
          current; a customer looking at stale links is the person who needs it. */}
      <div className="mb-4">
        <SiteDiscovery />
      </div>
      <SectionCard
        icon={LinkIcon}
        title="Website Links"
        subtitle="Links with PDF extensions are added to Documents automatically."
        action={(
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setSettingsModalOpen(true)}>Settings</Button>
            <Button size="sm" onClick={() => setModalOpen(true)}>Add links</Button>
          </div>
        )}
        bodyClassName="px-2 pb-2"
      >
        <DataTable<ILink>
          data={allLinks}
          columns={columns}
          rowKey={(row) => row.no}
          loading={{
            isLoading: isFetching,
            skeletonCount: 5,
            message: 'Loading links...',
          }}
          empty={{
            icon: LinkIcon,
            title: 'No links found',
            description: 'Add a link to get started',
          }}
          ariaLabel="Training links"
        />
        <Pagination
          className='my-4'
          onPageChange={handlePageChange}
          totalPage={linkData?.totalPages}
          page={queryParams?.page}
          limit={queryParams?.limit}
        />
      </SectionCard>

      {/* Refresh Settings modal */}
      <Dialog
        open={settingsModalOpen}
        onOpenChange={(open) => {
          setSettingsModalOpen(open);
          if (!open) setValidationError('');
        }}
      >
        <DialogContent className="sm:max-w-[550px]">
          <DialogHeader>
            <DialogTitle>Refresh Settings</DialogTitle>
            <DialogDescription>
              By default, all URLs refresh every 7 days. Leave the pattern empty to update all URLs,
              or enter a pattern (e.g. <strong>blog</strong> or <strong>products</strong>, without
              slashes) to target matching URLs.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label htmlFor="url-pattern">URL Pattern</Label>
              <Input
                id="url-pattern"
                type="text"
                value={selectedPattern}
                onChange={(e) => handlePatternChange(e.target.value)}
                placeholder="e.g. blog or products (without slashes)"
                className="mt-1.5"
              />
              <p className="mt-1 text-xs text-gray-500">
                {selectedPattern
                  ? `Will match URLs containing "${selectedPattern}"`
                  : 'Leave empty to apply settings to all URLs'}
              </p>
            </div>

            <div>
              <Label htmlFor="refresh-interval">Refresh Interval (days)</Label>
              <Input
                id="refresh-interval"
                type="number"
                value={refreshInterval}
                onChange={(e) => handleRefreshIntervalChange(Number(e.target.value))}
                min={selectedPattern ? 1 : 7}
                className="mt-1.5"
                style={{ appearance: 'textfield' }}
              />
              <p className="mt-1 text-xs text-gray-500">
                {selectedPattern
                  ? 'URLs matching this pattern refresh at this interval (minimum 1 day)'
                  : 'All URLs refresh at this interval (minimum 7 days)'}
              </p>
              {validationError && <p className="mt-1 text-sm text-red-500">{validationError}</p>}
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setSettingsModalOpen(false);
                setValidationError('');
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleSaveSettings} disabled={!!validationError || isLoading}>
              {isLoading ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Links modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="sm:max-w-[550px]">
          <DialogHeader>
            <DialogTitle>Add link</DialogTitle>
          </DialogHeader>
          <AddLinkForm
            links={links}
            setLinks={setLinks}
            arrLinks={arrLinks}
            setArrLinks={setArrLinks}
            onClearUrls={clearUrlsInParent}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button onClick={onTraining}>Start training</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AddLinks;
